// src/pages/Admin/AddFruit.jsx
import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  AddPhotoAlternate,
  Delete,
  Save,
  Cancel,
  CloudUpload,
  Category,
  Scale,
  AttachMoney,
  Inventory,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const AddFruit = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    scientificName: '',
    category: '',
    variety: '',
    description: '',
    pricePerKg: '',
    minimumOrder: '',
    quantity: '',
    origin: '',
    seasonality: 'year-round',
    tasteProfile: '',
    nutritionalInfo: '',
    storageRequirements: '',
    shelfLife: '',
    certifications: [],
    images: [],
    status: 'active'
  });

  const steps = ['Basic Information', 'Pricing & Stock', 'Details & Images'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // In a real app, you would upload to cloud storage
    // For now, we'll use placeholder URLs
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setFormData({
      ...formData,
      images: [...formData.images, ...imageUrls]
    });
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({
      ...formData,
      images: newImages
    });
  };

  const handleCertificationAdd = (e) => {
    if (e.key === 'Enter' && e.target.value) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, e.target.value]
      });
      e.target.value = '';
    }
  };

  const removeCertification = (index) => {
    const newCertifications = [...formData.certifications];
    newCertifications.splice(index, 1);
    setFormData({
      ...formData,
      certifications: newCertifications
    });
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.createFruit(formData);
      toast.success('Fruit added successfully!');
      navigate('/admin/fruits');
    } catch (error) {
      console.error('Error adding fruit:', error);
      toast.error('Failed to add fruit');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fruit Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scientific Name"
                name="scientificName"
                value={formData.scientificName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  <MenuItem value="citrus">Citrus</MenuItem>
                  <MenuItem value="tropical">Tropical</MenuItem>
                  <MenuItem value="berries">Berries</MenuItem>
                  <MenuItem value="stone_fruits">Stone Fruits</MenuItem>
                  <MenuItem value="melons">Melons</MenuItem>
                  <MenuItem value="exotic">Exotic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Variety"
                name="variety"
                value={formData.variety}
                onChange={handleChange}
                placeholder="e.g., Valencia, Navel, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                required
              />
            </Grid>
          </Grid>
        );
      
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price per Kg (USD)"
                name="pricePerKg"
                value={formData.pricePerKg}
                onChange={handleChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Order (kg)"
                name="minimumOrder"
                value={formData.minimumOrder}
                onChange={handleChange}
                type="number"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Available Quantity (kg)"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                type="number"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Origin"
                name="origin"
                value={formData.origin}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Seasonality</InputLabel>
                <Select
                  name="seasonality"
                  value={formData.seasonality}
                  onChange={handleChange}
                  label="Seasonality"
                >
                  <MenuItem value="year-round">Year-round</MenuItem>
                  <MenuItem value="summer">Summer</MenuItem>
                  <MenuItem value="winter">Winter</MenuItem>
                  <MenuItem value="spring">Spring</MenuItem>
                  <MenuItem value="autumn">Autumn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Taste Profile"
                name="tasteProfile"
                value={formData.tasteProfile}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="e.g., Sweet, tangy, aromatic"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nutritional Info"
                name="nutritionalInfo"
                value={formData.nutritionalInfo}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="e.g., High in Vitamin C, antioxidants"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Storage Requirements"
                name="storageRequirements"
                value={formData.storageRequirements}
                onChange={handleChange}
                placeholder="e.g., 2-4°C, high humidity"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shelf Life"
                name="shelfLife"
                value={formData.shelfLife}
                onChange={handleChange}
                placeholder="e.g., 2-3 weeks"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Certifications (Press Enter to add)"
                onKeyDown={handleCertificationAdd}
                placeholder="e.g., Organic, Fair Trade, GLOBALG.A.P."
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {formData.certifications.map((cert, index) => (
                  <Chip
                    key={index}
                    label={cert}
                    onDelete={() => removeCertification(index)}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddPhotoAlternate /> Upload Images
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="fruit-image-upload"
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="fruit-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      fullWidth
                    >
                      Upload Images
                    </Button>
                  </label>
                  
                  {formData.images.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {formData.images.length} image(s) uploaded
                      </Typography>
                      <Grid container spacing={2}>
                        {formData.images.map((img, index) => (
                          <Grid item xs={6} md={3} key={index}>
                            <Box sx={{ position: 'relative' }}>
                              <img
                                src={img}
                                alt={`Fruit ${index}`}
                                style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => removeImage(index)}
                                sx={{ 
                                  position: 'absolute', 
                                  top: 4, 
                                  right: 4, 
                                  bgcolor: 'rgba(0,0,0,0.5)',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Make sure all information is accurate before submitting. 
                This information will be visible to potential buyers.
              </Alert>
            </Grid>
          </Grid>
        );
      
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/fruits')}
          sx={{ mb: 3 }}
        >
          Back to Fruits
        </Button>
        
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Add New Fruit
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add a new fruit product to the platform
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={() => navigate('/admin/fruits')}
            variant="outlined"
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Fruit'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              variant="contained"
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default AddFruit;